/** Utilities for z-stack selection and a physically motivated defocus fallback. */

export interface FocusPlane {
  /** Axial position of this focal plane, in micrometres. */
  zMicrometres: number;
  src: string;
}

export interface FocusPlaneSelection {
  lower: FocusPlane;
  upper: FocusPlane;
  /** Linear blend amount from lower to upper; 0 when both are the same plane. */
  blend: number;
}

export interface DefocusBlurInput {
  axialOffsetUm: number;
  numericalAperture: number;
  wavelengthNm?: number;
  maxBlurPx?: number;
}

export interface DefocusBlurResult {
  blurRadiusPx: number;
  depthOfFieldUm: number;
  chromaticShiftPx: number;
}

function assertFinite(name: string, value: number): void {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
}

/** Select adjacent focal planes for a smooth crossfade while focusing. */
export function selectFocusPlanes(planes: FocusPlane[], focusZMicrometres: number): FocusPlaneSelection {
  if (planes.length === 0) throw new RangeError('At least one focus plane is required');
  assertFinite('focusZMicrometres', focusZMicrometres);
  planes.forEach((plane) => {
    assertFinite('plane.zMicrometres', plane.zMicrometres);
    if (!plane.src) throw new RangeError('Every focus plane must have an image source');
  });

  const sorted = [...planes].sort((a, b) => a.zMicrometres - b.zMicrometres);
  if (sorted.length === 1 || focusZMicrometres <= sorted[0].zMicrometres) {
    return { lower: sorted[0], upper: sorted[0], blend: 0 };
  }
  const last = sorted[sorted.length - 1];
  if (focusZMicrometres >= last.zMicrometres) return { lower: last, upper: last, blend: 0 };

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const lower = sorted[index];
    const upper = sorted[index + 1];
    if (focusZMicrometres >= lower.zMicrometres && focusZMicrometres <= upper.zMicrometres) {
      const span = upper.zMicrometres - lower.zMicrometres;
      return { lower, upper, blend: span === 0 ? 0 : (focusZMicrometres - lower.zMicrometres) / span };
    }
  }

  return { lower: last, upper: last, blend: 0 };
}

/**
 * Approximate defocus using diffraction-limited depth of field. Blur increases
 * with axial displacement and NA; the capped chromatic shift adds subtle color
 * fringing for CSS/canvas rendering. It is an educational model, not a ray trace.
 */
export function calculateDefocusBlur(input: DefocusBlurInput): DefocusBlurResult {
  assertFinite('axialOffsetUm', input.axialOffsetUm);
  if (!Number.isFinite(input.numericalAperture) || input.numericalAperture <= 0) {
    throw new RangeError('numericalAperture must be finite and positive');
  }
  const wavelengthNm = input.wavelengthNm ?? 550;
  const maxBlurPx = input.maxBlurPx ?? 12;
  if (!Number.isFinite(wavelengthNm) || wavelengthNm <= 0) throw new RangeError('wavelengthNm must be finite and positive');
  if (!Number.isFinite(maxBlurPx) || maxBlurPx < 0) throw new RangeError('maxBlurPx must be finite and non-negative');

  const wavelengthUm = wavelengthNm / 1000;
  const depthOfFieldUm = wavelengthUm / (input.numericalAperture ** 2);
  const normalizedDefocus = Math.abs(input.axialOffsetUm) / depthOfFieldUm;
  const blurRadiusPx = Math.min(maxBlurPx, normalizedDefocus * 1.5);

  return {
    blurRadiusPx,
    depthOfFieldUm,
    chromaticShiftPx: Math.min(2, blurRadiusPx * 0.12),
  };
}
